import { useState, useCallback } from 'react';
import { SceneWithUserNames } from '../../types/scene.types';
import { useSceneUpdates } from '../../hooks/dataUpdates/useSceneUpdates';

export interface MultiColumnFindReplacePosition {
  top: number;
  left: number;
}

// 多列数据结构
export interface MultiColumnData {
  sceneSet: string;
  scenePlot: string;
  sceneLocation: string;
  scenePeriod: string;
  scenePlotSummary: string;
}

// 场次元素查找替换数据结构
export interface ElementFindReplaceData {
  elementName: string;
  elementAbbr: string;
  elementType?: string;
}

export const useMultiColumnFindReplace = (
  getScenes: () => SceneWithUserNames[], // 改为函数，每次调用时获取最新场次行数据
  updateScenes: (updatedScenes: SceneWithUserNames[], updatedDisplayTexts?: { [sceneId: string]: string }) => void,
  setSceneLocationDisplayTexts?: React.Dispatch<React.SetStateAction<{ [sceneId: string]: string }>>,
  breakdownId?: string // 添加 breakdownId 参数，用于调用统一的更新 hook（分场 ID）
) => {
  const { updateScenesData } = useSceneUpdates();
  const [findReplaceVisible, setFindReplaceVisible] = useState(false);
  const [findReplacePosition, setFindReplacePosition] = useState<MultiColumnFindReplacePosition>({ top: 0, left: 0 });

  // 关闭查找替换弹窗
  const handleCloseFindReplace = useCallback(() => {
    setFindReplaceVisible(false);
  }, []);

  // 获取多列数据（直接返回结构化数据，而不是合并字符串）
  const getMultiColumnData = useCallback((): MultiColumnData[] => {
    const scenes = getScenes(); // 每次调用时获取最新数据
    return scenes.map(scene => ({
      sceneSet: scene.sceneSet || '',
      scenePlot: scene.scenePlot || '',
      sceneLocation: scene.sceneLocation || '',
      scenePeriod: scene.scenePeriod || '',
      scenePlotSummary: scene.scenePlotSummary || ''
    }));
  }, [getScenes]);

  // 查找替换数据更新处理（直接接收结构化的多列数据）
  const handleFindReplaceUpdate = useCallback(async (updatedData: MultiColumnData[]) => {
    // 从函数中获取最新的 scenes 数据，而不是使用闭包中的旧值
    const latestScenes = getScenes(); // 每次调用时获取最新数据
    // 保存更新前的状态，用于错误回滚
    const previousScenes = [...latestScenes];

    // 准备批量更新到服务器的数据
    const updates: Array<{
      sceneID: string;
      sceneSet?: string;
      scenePlot?: string;
      sceneLocation?: string;
      scenePeriod?: string;
      scenePlotSummary?: string;
    }> = [];

    // 查找数据会将空值标准化为 ''。比较时也做同样的标准化，
    // 避免把 undefined/null 与 '' 误判为用户更改。
    const normalizeText = (value: string | null | undefined) => value ?? '';

    latestScenes.forEach((scene, index) => {
      if (index < updatedData.length) {
        const data = updatedData[index];
        const update = {
          sceneID: scene.sceneID,
          ...(normalizeText(scene.sceneSet) !== data.sceneSet && { sceneSet: data.sceneSet }),
          ...(normalizeText(scene.scenePlot) !== data.scenePlot && { scenePlot: data.scenePlot }),
          ...(normalizeText(scene.sceneLocation) !== data.sceneLocation && { sceneLocation: data.sceneLocation }),
          ...(normalizeText(scene.scenePeriod) !== data.scenePeriod && { scenePeriod: data.scenePeriod }),
          ...(normalizeText(scene.scenePlotSummary) !== data.scenePlotSummary && { scenePlotSummary: data.scenePlotSummary })
        };

        if (Object.keys(update).length > 1) {
          updates.push(update);
        }
      }
    });

    const updatesBySceneId = new Map(updates.map(({ sceneID, ...data }) => [sceneID, data]));
    const updatedScenes = latestScenes.map(scene => {
      const update = updatesBySceneId.get(scene.sceneID);
      return update ? { ...scene, ...update } : scene;
    });

    // 仅对真正发生变化的地点同步显示文本缓存。
    const updatedDisplayTexts: { [sceneId: string]: string } = {};
    updates.forEach(update => {
      if (update.sceneLocation !== undefined) {
        updatedDisplayTexts[update.sceneID] = update.sceneLocation;
      }
    });

    // 先更新本地状态（乐观更新）
    updateScenes(updatedScenes, updatedDisplayTexts);

    if (setSceneLocationDisplayTexts && Object.keys(updatedDisplayTexts).length > 0) {
      setSceneLocationDisplayTexts(prev => ({
        ...prev,
        ...updatedDisplayTexts
      }));
    }

    // 调用批量更新API（使用统一的更新 hook）
    if (updates.length > 0 && breakdownId) {
      try {
        const updateArray = updates.map(u => ({
          sceneId: u.sceneID,
          data: {
            ...(u.sceneSet !== undefined && { sceneSet: u.sceneSet }),
            ...(u.scenePlot !== undefined && { scenePlot: u.scenePlot }),
            ...(u.sceneLocation !== undefined && { sceneLocation: u.sceneLocation }),
            ...(u.scenePeriod !== undefined && { scenePeriod: u.scenePeriod }),
            ...(u.scenePlotSummary !== undefined && { scenePlotSummary: u.scenePlotSummary }),
          } as any,
        }));
        await updateScenesData(updateArray, breakdownId);
        console.log('批量更新多列成功:', updates.length, '个场次');
      } catch (error) {
        console.error('批量更新多列失败:', error);
        // 失败时回滚本地状态
        updateScenes(previousScenes, undefined);
        // 回滚地点显示文本缓存
        if (setSceneLocationDisplayTexts) {
          const previousDisplayTexts: { [sceneId: string]: string } = {};
          previousScenes.forEach(scene => {
            previousDisplayTexts[scene.sceneID] = scene.sceneLocation || '';
          });
          setSceneLocationDisplayTexts(prev => ({
            ...prev,
            ...previousDisplayTexts
          }));
        }
        throw error;
      }
    }
  }, [getScenes, updateScenes, setSceneLocationDisplayTexts, breakdownId, updateScenesData]);

  return {
    findReplaceVisible,
    findReplacePosition,
    setFindReplaceVisible,
    setFindReplacePosition,
    handleCloseFindReplace,
    getMultiColumnData,
    handleFindReplaceUpdate
  };
};
